import { addDays } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { LoggedHourHo, useUpdateLoggedHourMutation } from '@/graphql';
import { dateToHHMM, getHoursMinutesFormattedDate, HHMMToMinutes } from '@/lib/formatTime';
import Notistack from '@/lib/notistack';

import LoggedHoursForm, { LoggedHoursFormData } from './LoggedHoursForm';

interface EditLoggedHoursProps {
  loggedHour?: LoggedHourHo;
  onSuccess?: () => void;
}

function EditLoggedHours({ loggedHour, onSuccess }: EditLoggedHoursProps) {
  const { t } = useTranslation();
  const [updateLoggedHour] = useUpdateLoggedHourMutation();

  const handleSubmit = (data: LoggedHoursFormData, callback?: (success: boolean) => void) => {
    const startTime = HHMMToMinutes(data.start);
    const endTime = HHMMToMinutes(data.end);

    const endDate = endTime < startTime ? addDays(data.date, 1) : data.date;

    let declarableTime = null;

    if (data.declarableTimeHours) {
      declarableTime = data.declarableTimeHours * 60;
    }

    if (data.declarableTimeMinutes) {
      declarableTime = declarableTime ? declarableTime + data.declarableTimeMinutes : data.declarableTimeMinutes;
    }

    updateLoggedHour({
      variables: {
        id: loggedHour!.id,
        categoryId: data.category?.id,
        break: HHMMToMinutes(data.break),
        startDatetime: getHoursMinutesFormattedDate(data.date, HHMMToMinutes(data.start) ? data.start : dateToHHMM()),
        endDatetime: getHoursMinutesFormattedDate(endDate, HHMMToMinutes(data.end) ? data.end : dateToHHMM()),
        declarableTime: loggedHour!.addendum && declarableTime ? declarableTime : null,
        description: data.description,
        projectId: data.project?.id,
        tenantId: data.tenant?.id,
        status: null,
        declarable: data.declarable,
        createdAt: data.createdAt ? new Date(data.createdAt) : undefined,
        updatedAt: data.updatedAt ? new Date(data.updatedAt) : undefined,
      },
    })
      .then(() => {
        Notistack.toast?.(t('logHours:message.success.edit'), { variant: 'success' });
        onSuccess?.();
        callback?.(true);
      })
      .catch(({ message }) => {
        Notistack.toast?.(t([message, 'logHours:message.error.edit']), { variant: 'error' });
        callback?.(false);
      });
  };

  return <LoggedHoursForm loggedHour={loggedHour} handleSubmit={handleSubmit} />;
}

export default EditLoggedHours;
